import {
  AnalyzerManagementSubType,
  assetConfigurations,
  AssetManagementSubType,
  AssetTypeAPI,
  capitalizeFirstLetter,
  ComputeSubType,
  EnvSubType,
  FieldSubType,
  insertObjIf,
  NetworkSubType,
  PlcDcsSubType,
  SecuritySubType,
} from '@mss/mss-component-library';
import { TFunction } from 'i18next';
import React from 'react';
import {
  applicationViewAny,
  assetsViewAny,
} from '../authorization/util/permissionSets';
import { assetTypeAPItoURI } from '../common/AssetPages/utils/conversions';
import PolarChart from './components/cards/PolarChart';
import RemoteSessionChart from './components/cards/RemoteSessionChart';
import StatusLine from './components/StatusLine';
import AssetCollectorCompactTable from './components/tables/AssetCollectorCompactTable';
import AssetCompactTable from './components/tables/AssetCompactTable';
import AssetMonitorCompactTable from './components/tables/AssetMonitorCompactTable';
import AutomationCompactTable from './components/tables/AutomationCompactTable';
import RemoteSessionCompactTable from './components/tables/RemoteSessionCompactTable';

export type AssetProps = {
  id: string;
  name: string;
  type: string;
  asset_type: string;
  asset_id: string;

  // Asset sub types
  application_type: SecuritySubType | undefined;
  dcs_plc_type: PlcDcsSubType | undefined;
  analyzermanagement_type: AnalyzerManagementSubType | undefined;
  assetmanagement_type: AssetManagementSubType | undefined;
  compute_type: ComputeSubType | undefined;
  env_type: EnvSubType | undefined;
  field_type: FieldSubType | undefined;
  network_type: NetworkSubType | undefined;
};

const BASE_ROUTE = (asset: AssetProps): string =>
  '/{RESOURCE}/{TYPE}/{SUB_TYPE}/{ID}'
    .replace('{RESOURCE}', resourceType(asset))
    .replace('{TYPE}', assetType(asset))
    .replace('{SUB_TYPE}', assetSubType(asset))
    .replace('{ID}', asset.id);

export const ROUTES = {
  detail: (asset: AssetProps): string => `${BASE_ROUTE(asset)}/asset-settings`,
  collectors: (asset: AssetProps): string =>
    `${BASE_ROUTE(asset)}/collection-settings`,
  monitors: (asset: AssetProps): string =>
    `${BASE_ROUTE(asset)}/monitor-settings`,
};

export const resourceType = (asset: AssetProps): string => {
  switch (assetType(asset)) {
    case 'analyzermanagement':
    case 'assetmanagement':
    case 'security':
    case 'control':
    case 'virtualization':
      return 'applications';
    default:
      return 'assets';
  }
};

const assetType = (asset: AssetProps): string => {
  const asset_type = asset.type;
  switch (asset_type) {
    case 'dcs_plc':
      return 'plcdcs';
    case 'application':
      return 'security';
    case 'fieldprm':
      return 'field';
    default:
      return asset_type.replace('_', '');
  }
};

const assetSubType = (asset: any): string => {
  const type = assetType(asset);
  const subType = asset[`${type}_type`];
  switch (type) {
    case 'security':
      return asset.application_type;
    case 'plcdcs':
      return asset.dcs_plc_type;
    case 'field':
      return 'fieldprm';
    default:
      return subType;
  }
};

export type TooltipTitle = Record<StatusTypes, Record<string, string>>;

export const tooltipTitle = (t: TFunction): TooltipTitle => {
  return {
    Assets: {
      total: t('Total amount of assets and applications onboarded to MSS'),
      up: t('Assets and applications with heartbeat status up'),
      down: t('Assets and applications with heartbeat status down'),
      off: t('Assets and applications with heartbeat status turned off'),
    },
    Collectors: {
      total: t('Total amount of collectors'),
      success: t('Collectors with last status success'),
      failed: t('Collectors with last status failed'),
      disabled: t('Collectors that are disabled because the heartbeat is down'),
      'not run': t('Collectors that did not run yet'),
      off: t('Collectors that are turned off'),
    },
    Monitors: {
      total: t('Total amount of monitors'),
      ok: t('Monitors with last log status OK'),
      error: t('Monitors with last log status error'),
      unknown: t('Monitors with last log status unknown'),
      off: t('Monitors that are turned off'),
    },
    'Remote Sessions': {
      active: t('Total amount of active remote sessions'),
    },
    Automations: {
      total: t('Total amount of automations'),
      enabled: t('Automations that are enabled'),
      disabled: t('Automations that are disabled'),
    },
  };
};

export const onTableChange =
  (
    assetFilters: string[],
    setAssetFilters: React.Dispatch<React.SetStateAction<string[]>>,
    setSubTypeFilters: React.Dispatch<React.SetStateAction<string[]>>,
    setRoleFilters: React.Dispatch<React.SetStateAction<string[]>>,
    setLocationFilters: React.Dispatch<React.SetStateAction<string[]>>,
    setCustodianFilters: React.Dispatch<React.SetStateAction<string[]>>
  ) =>
  (
    _: any,
    filters: {
      subType: string[] | null;
      type: string[] | null;
      custodian_id: string[] | null;
      location: string[] | null;
      role: string[] | null;
    }
  ) => {
    const removedAssetFilters = assetFilters.filter(
      f => !filters.type?.includes(f)
    );
    if (
      removedAssetFilters.length ||
      (!assetFilters.length && filters.type?.length)
    ) {
      const sourceFilters = removedAssetFilters.length
        ? removedAssetFilters
        : (filters.type ?? []);
      setSubTypeFilters(prev => {
        const relatedSubTypes = sourceFilters
          .map((x: string) =>
            Object.values(
              assetConfigurations[assetTypeAPItoURI[x as AssetTypeAPI]].assets
            ).map(c => c.type as string)
          )
          .flat();

        return prev.filter(f => !relatedSubTypes.includes(f));
      });
    } else {
      setSubTypeFilters(filters.subType ?? []);
    }

    setAssetFilters(filters.type ?? []);
    setRoleFilters(filters.role ?? []);
    setLocationFilters(filters.location ?? []);
    setCustodianFilters(filters.custodian_id ?? []);
  };

export const mapResource = (resource: string): string => {
  switch (resource) {
    case 'analyzer_management':
      return 'Analyzer Management';
    case 'application':
      return 'Security';
    case 'asset_management':
      return 'Asset Management';
    case 'dcs_plc':
      return 'PLC/DCS';
    default:
      return capitalizeFirstLetter(resource);
  }
};

type StatusTypes =
  | 'Assets'
  | 'Collectors'
  | 'Monitors'
  | 'Remote Sessions'
  | 'Automations';

export type StatusCard = {
  count: number;
  type: string;
  url: string;
};

export type Status = {
  name: StatusTypes;
  cards: StatusCard[];
};

export type TableComponent = React.ComponentType<{
  resources: Record<string, any>[];
  loading: boolean;
}>;

export type ChartComponent = React.ComponentType<{
  status: Status;
  colors: string[];
  loading: boolean;
}>;

export type MappedStatus = {
  cards: StatusCard[];
  name: StatusTypes;
  TableComponent: TableComponent;
  ChartComponent: ChartComponent;
  tooltips: Record<string, string>;
  colors: string[];
  totalKey?: string;
  licenseKey?: string;
  status: Status;
  areAssets?: boolean;
};

const chartColors: Record<StatusTypes, string[]> = {
  Assets: ['#5EBA00', '#FA4654', '#CCCCCC'],
  Collectors: ['#5EBA00', '#FA4654', '#17A2B8', '#F1C40F', '#CCCCCC'],
  Monitors: ['#5EBA00', '#FA4654', '#F1C40F', '#CCCCCC'],
  'Remote Sessions': ['#17A2B8'],
  Automations: ['#5EBA00', '#CCCCCC'],
};

export const mapStatus = (
  status: Status,
  t: TFunction
): Omit<MappedStatus, 'status'> => {
  const defaultStatus: Pick<
    MappedStatus,
    'tooltips' | 'name' | 'colors' | 'cards'
  > = {
    ...status,
    tooltips: tooltipTitle(t)[status.name as StatusTypes],
    colors: chartColors[status.name as StatusTypes],
    name: t(status.name),
  };
  switch (status.name) {
    case 'Collectors':
      return {
        TableComponent: AssetCollectorCompactTable,
        ChartComponent: PolarChart,
        areAssets: true,
        ...defaultStatus,
      };
    case 'Monitors':
      return {
        TableComponent: AssetMonitorCompactTable,
        ChartComponent: PolarChart,
        areAssets: true,
        ...defaultStatus,
      };
    case 'Remote Sessions':
      return {
        TableComponent: RemoteSessionCompactTable,
        ChartComponent: RemoteSessionChart,
        totalKey: 'active',
        licenseKey: 'remoteaccessconnect',
        ...defaultStatus,
      };
    case 'Automations':
      return {
        TableComponent: AutomationCompactTable,
        ChartComponent: PolarChart,
        ...defaultStatus,
        licenseKey: 'automations',
      };
    case 'Assets':
    default:
      return {
        TableComponent: AssetCompactTable,
        ChartComponent: PolarChart,
        areAssets: true,
        ...defaultStatus,
      };
  }
};

export const defaultAssetsStatus = {
  name: 'Assets',
  cards: [
    {
      type: 'total',
    },
    {
      type: 'up',
    },
    {
      type: 'down',
    },
    {
      type: 'off',
    },
  ],
};

export const defaultCollectorsStatus = {
  name: 'Collectors',
  cards: [
    {
      type: 'total',
    },
    {
      type: 'success',
    },
    {
      type: 'failed',
    },
    {
      type: 'disabled',
    },
    {
      type: 'not run',
    },
    {
      type: 'off',
    },
  ],
};

export const defaultMonitorsStatus = {
  name: 'Monitors',
  cards: [
    {
      type: 'total',
    },
    {
      type: 'ok',
    },
    {
      type: 'error',
    },
    {
      type: 'unknown',
    },
    {
      type: 'off',
    },
  ],
};

export const defaultRemoteSessionsStatus = {
  name: 'Remote Sessions',
  cards: [
    {
      type: 'active',
    },
  ],
};

export const defaultAutomationsStatus = {
  name: 'Automations',
  cards: [
    {
      type: 'total',
    },
    {
      type: 'enabled',
    },
    {
      type: 'disabled',
    },
  ],
};

const assetPermissions = [...assetsViewAny, ...applicationViewAny];
export const statusConfigs = (
  unidrectional?: boolean
): Record<
  string,
  Omit<React.ComponentProps<typeof StatusLine>, 'endpoint'>
> => ({
  assets: {
    defaultValue: defaultAssetsStatus as Status,
    permissions: assetPermissions,
  },
  collectors: {
    defaultValue: defaultCollectorsStatus as Status,
    permissions: assetPermissions,
  },
  ...insertObjIf(!unidrectional, {
    monitors: {
      defaultValue: defaultMonitorsStatus as Status,
      permissions: ['view_automationmonitorasset'],
    },
    'remote-sessions': {
      defaultValue: defaultRemoteSessionsStatus as Status,
      permissions: ['admin_view_remotesession'],
    },
  }),
  automations: {
    defaultValue: defaultAutomationsStatus as Status,
    permissions: ['view_automation'],
  },
});
